import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "./AdminLayout";
import { adminPost, getAdminSession } from "./adminApi";
import "./admin.css";

const MAX_RULES = 6;
const MAX_RULE_LEN = 200;

const splitTimer = (timer) => {
  const [m, s] = String(timer || "03:00").split(":");
  return { minutes: String(parseInt(m, 10) || 1), seconds: String(parseInt(s, 10) || 0).padStart(2, "0") };
};

// Port of rules.php: per-theme text, rules, points, colors and timer.
export default function AdminRules() {
  const { themeName } = useParams(); // theme id
  const navigate = useNavigate();
  const session = getAdminSession();

  const [loaded, setLoaded] = useState(false);
  const [gameTitle, setGameTitle] = useState("");
  const [title, setTitle] = useState("");
  const [thankYou, setThankYou] = useState("");
  const [rules, setRules] = useState([""]);
  const [points, setPoints] = useState("1");
  const [bonusPoints, setBonusPoints] = useState("1");
  const [color, setColor] = useState("#000000");
  const [color1, setColor1] = useState("#ed695d");
  const [color2, setColor2] = useState("#000000");
  const [color3, setColor3] = useState("#a78e8e");
  const [minutes, setMinutes] = useState("3");
  const [seconds, setSeconds] = useState("00");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!session) {
      navigate("/admin", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await adminPost("fetchThemeData", { currentTheme: themeName }, session);
        const d = res.data || {};
        setGameTitle(d.game_title || "");
        setTitle(d.landing_page_title || "");
        setThankYou(d.custom_text_thank_you_page || "");
        setRules(Array.isArray(d.rules) && d.rules.length ? d.rules : [""]);
        setPoints(String(d.points ?? "1"));
        setBonusPoints(String(d.bonus_points ?? "1"));
        setColor(d.landing_page_title_color || "#000000");
        setColor1(d.button_color || "#ed695d");
        setColor2(d.button_text_color || "#000000");
        setColor3(d.candy_bg_color || "#a78e8e");
        const t = splitTimer(d.timer);
        setMinutes(t.minutes);
        setSeconds(t.seconds);
        setLoaded(true);
      } catch (err) {
        Swal.fire({ icon: "error", title: err.message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeName]);

  if (!session) return null;

  const warn = (title, text) => Swal.fire({ icon: "error", title, text });
  const blockInvalidKeys = (e) => ["e", "E", "+", "-", ".", ","].includes(e.key) && e.preventDefault();
  // digits only (handles paste), capped at 100
  const clampPoints = (v) => {
    const digits = v.replace(/[^0-9]/g, "");
    return digits !== "" && parseInt(digits, 10) > 100 ? "100" : digits;
  };

  const normalizeMinutes = () => {
    const n = parseInt(minutes, 10);
    setMinutes(String(!n || n < 1 ? 1 : n));
  };
  const normalizeSeconds = (value) => {
    let n = parseInt(value, 10);
    if (isNaN(n) || n < 0) n = 0;
    if (n > 59) n = 59;
    return String(n).padStart(2, "0");
  };

  const save = async () => {
    const trimmedRules = rules.map((r) => r.trim());
    if (trimmedRules.length === 0) return warn("At least 1 rule required");
    if (trimmedRules.some((r) => !r)) return warn("Please fill in all rule fields.");
    if (trimmedRules.some((r) => r.length > MAX_RULE_LEN)) return warn("Rules must below 200 characters.");
    if (!gameTitle.trim()) return warn("Game tab name required", "Game tab name cannot be empty.");
    if (!title.trim()) return warn("Title Required", "Title cannot be empty.");
    for (const [label, value] of [["Correct points", points], ["Bonus points", bonusPoints]]) {
      const n = Number(value);
      if (value.trim() === "") return warn(`${label} Required`, `${label} cannot be empty.`);
      if (isNaN(n)) return warn("Invalid Input", `Please enter a valid number for ${label.toLowerCase()}.`);
      if (n <= 0) return warn("Value must be greater than 0", `${label} must be greater than 0.`);
      if (n > 100) return warn("Value Too Large", `${label} cannot be more than 100.`);
    }
    if (!parseInt(minutes, 10) || parseInt(minutes, 10) < 1) return warn("Minutes must be at least 1");

    setBusy(true);
    try {
      await adminPost(
        "saveRulesData",
        {
          currentTheme: themeName,
          data: {
            rules: trimmedRules,
            title: title.trim(),
            game_title: gameTitle.trim(),
            thank_you_title: thankYou,
            time: `${parseInt(minutes, 10)}:${normalizeSeconds(seconds)}`,
            color,
            color1,
            color2,
            color3,
            points: Number(points),
            bonus_points: Number(bonusPoints),
          },
        },
        session
      );
      await Swal.fire({ icon: "success", title: "Data Updated", timer: 1000, showConfirmButton: false });
      navigate(`/admin/candy-icons/${encodeURIComponent(themeName)}`);
    } catch (err) {
      warn(err.message);
    } finally {
      setBusy(false);
    }
  };

  const setRule = (i, value) => setRules((rs) => rs.map((r, idx) => (idx === i ? value : r)));

  return (
    <AdminLayout session={session} title="Settings" backTo="/admin/themes">
      {!loaded ? (
        <p className="adm-card">Loading...</p>
      ) : (
        <div className="adm-two-col">
          <div className="adm-card">
            <h4>Add Custom Game Tab Name:</h4>
            <textarea className="adm-input" maxLength={20} value={gameTitle} onChange={(e) => setGameTitle(e.target.value)} />
            <p className="adm-note">
              * Maximum character limit should be 20 characters.<br />
              * This title will appear as the game name in the browser tab on all game pages.
            </p>

            <h4>Add Custom Title:</h4>
            <textarea className="adm-input" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
            <p className="adm-note">
              * Maximum character limit should be 200 characters.<br />
              * This title will reflect on the main game page
            </p>

            <h4>Custom Text Thank You Page:</h4>
            <textarea className="adm-input" maxLength={300} value={thankYou} onChange={(e) => setThankYou(e.target.value)} />
            <p className="adm-note">
              * This text will reflect on the thank you page.<br />
              * Maximum character limit should be 300 characters.
            </p>

            <h4>Add Custom Rules</h4>
            {rules.map((rule, i) => (
              <div className="adm-rule-row" key={i}>
                <textarea
                  className="adm-input"
                  placeholder="Rules"
                  value={rule}
                  onChange={(e) => setRule(i, e.target.value)}
                />
                <button
                  type="button"
                  className="adm-btn adm-btn-danger"
                  aria-label="Delete rule"
                  onClick={() => setRules((rs) => rs.filter((_, idx) => idx !== i))}
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              type="button"
              className="adm-btn"
              disabled={rules.length >= MAX_RULES}
              onClick={() => setRules((rs) => [...rs, ""])}
            >
              + Add new rule
            </button>
            <p className="adm-note">
              * Keep 6 rules at the most<br />
              * At least 1 rule is required<br />
              * Character limit of each rule is 200
            </p>
          </div>

          <div className="adm-card">
            <h4>Page Content</h4>
            <div className="adm-field">
              <label>Correct Points</label>
              <input
                type="number" min="1" max="100" step="1" className="adm-input adm-input-sm"
                value={points} onKeyDown={blockInvalidKeys} onChange={(e) => setPoints(clampPoints(e.target.value))}
              />
            </div>
            <div className="adm-field">
              <label>Bonus Points</label>
              <input
                type="number" min="1" max="100" step="1" className="adm-input adm-input-sm"
                value={bonusPoints} onKeyDown={blockInvalidKeys} onChange={(e) => setBonusPoints(clampPoints(e.target.value))}
              />
            </div>
            <div className="adm-field">
              <label>Text Color</label>
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
            </div>
            <div className="adm-field">
              <label>Button Box Color</label>
              <input type="color" value={color1} onChange={(e) => setColor1(e.target.value)} />
            </div>
            <div className="adm-field">
              <label>Button text color</label>
              <input type="color" value={color2} onChange={(e) => setColor2(e.target.value)} />
            </div>
            <div className="adm-field">
              <label>Candy background Color</label>
              <input type="color" value={color3} onChange={(e) => setColor3(e.target.value)} />
            </div>

            <h4 className="adm-center-text">Timeout</h4>
            <div className="adm-timer">
              <label>Minutes:</label>
              <input
                type="number" min="1" className="adm-input adm-input-sm" value={minutes}
                onKeyDown={blockInvalidKeys} onChange={(e) => setMinutes(e.target.value)} onBlur={normalizeMinutes}
              />
              <label>Seconds:</label>
              <input
                type="number" min="0" max="59" className="adm-input adm-input-sm" value={seconds}
                onKeyDown={blockInvalidKeys} onChange={(e) => setSeconds(e.target.value)}
                onBlur={(e) => setSeconds(normalizeSeconds(e.target.value))}
              />
            </div>
            <p className="adm-timer-display">{(parseInt(minutes, 10) || 1)}:{normalizeSeconds(seconds)}</p>
          </div>

          <div className="adm-actions-row">
            <button type="button" className="adm-btn adm-btn-success" onClick={save} disabled={busy}>
              {busy ? "Saving..." : "Next »"}
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
